import _ from 'lodash'
import $ from 'jquery'
import Backbone from 'backbone'
import template from './field-list.hbs'
import rowTemplate from './field-list-row.hbs'
import i18n from 'scoreboard/components/i18n'
import formatValue from 'scoreboard/components/format-value'
import hasUncheckedCheckbox from 'scoreboard/components/has-unchecked-checkbox'
import FormView from 'scoreboard/components/form-view'
import dataTypeKey from '../data-type-key'
import SendTo from 'scoreboard/components/send-to'
import SendToOptions from 'scoreboard/components/send-to-options'
import Tooltip from 'scoreboard/components/tooltip'
import excelExport from 'scoreboard/components/export/excel-export'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import FieldAndGroupRenderer from '../field-and-group-renderer'
import unwrapModel from 'scoreboard/components/unwrap-model'
import highlight from 'scoreboard/components/highlight'
import Dataset from 'scoreboard/datasets/model/dataset'
import DatasetTable from 'scoreboard/datasets/model/dataset-table'
import clusterLabel from 'scoreboard/datasets/edit/fields/cluster-label'
import isDateTimeDataType from 'scoreboard/components/is-date-time-data-type'
import calendars from 'scoreboard/components/calendars'
import isGeographicOrRollup from '../is-geographic-or-rollup'
import DataType from 'scoreboard/datasets/model/data-type'
const { YES_NO, CALENDAR_PERIOD, CLUSTER } = DataType

function formatFieldValue (field, value) {
	var dataType = field.dataType
	var formattedValue = value
	if (isNumericDataType(dataType) || dataType === YES_NO) {
		if (!field.primaryKey) {
			formattedValue = formatValue({ value: value, dataType: dataType })
		}
	} else if (isDateTimeDataType(dataType)) {
		formattedValue = formatValue({ value, dataType })
	} else if (dataType === CLUSTER) {
		formattedValue = clusterLabel(value, field.clusterLabels)
	} else if (dataType === CALENDAR_PERIOD) {
		formattedValue = _.get(calendars.periodsById[value], 'label', '')
	}
	return formattedValue
}

function getExample(examples, fieldId) {
	var example = examples[fieldId]
	if (Dataset.isTotal(fieldId)) { // #55824
		example = '1'
	}
	return example
}

const FieldListFieldAndGroupRenderer = FieldAndGroupRenderer.extend({

	getFieldsHtml: function(fieldOptions) {
		let html = ''
		_.each(fieldOptions, fieldOption => {
			let field = this.model.getFieldByFieldIdentifier(fieldOption.id)
			let fieldIdentifier = fieldOption.id
			let example = getExample(this.options.examples, field.id)
			let labelHtml = _.escape(this.model.getFieldLabel(fieldOption))
			let datasetForField = unwrapModel(this.model.getDatasetForField(fieldOption))
			if (this.model.get('rootDatasetId') != datasetForField.id) {
				labelHtml = `<span class="linked-field-label">${labelHtml}</span>` +
					'<span class="linked-dataset-label">' +
					_.escape(datasetForField.label) +
					'</span>'
			}
			// #27079 only display primary keys in field list if they are rollup
			if (field.primaryKey && !isGeographicOrRollup(field.dataType)) {
				return
			}

			html += rowTemplate({
				// #39615 need the field identifier, not field ID
				fieldIdentifier,
				labelHtml,
				checked: this.options.checkedIds[fieldIdentifier],
				disabled: field.aggregationType === 'none',
				dataType: i18n.get(dataTypeKey(field.dataType)),
				example: formatFieldValue(field, example),
				readOnly: this.options.readOnly
			})
		})
		return html
	},

	getGroupHtml: function(group) {
		return `<tr class="table-body-subheader"><td colspan="4">${_.escape(group)}</td></tr>`
	}

})

var FieldList = Backbone.View.extend({

	tagName: 'form',

	className: 'field-list',

	events: {
		'change .toggle-all': 'clickToggleAll',
		'change tbody input[type=checkbox]': 'toggleToggleAll',
		'click table tr': 'toggleRow',
		'click .send-to': 'sendTo'
	},

	/** @type {Dataset} */
	model: null,

	/**
	 * A form that lists all fields in the dataset, and allows adding or removing
	 * multiple filters based on those fields at once.  Launched from the
	 * Datasets Overview.
	 *
	 * https://github.com/SpiderStrategies/Scoreboard/issues/13126
	 *
	 * https://basecamp.com/1824967/projects/4444406/messages/72478968
	 *
	 * @constructs
	 *
	 * @param options
	 *
	 * @param {Dataset} options.model the dataset
	 *
	 * @param {Object[]|DatasetTable[]} options.tables the tables currently visible on the Overview
	 *
	 * @param {Object} options.examples example values for each field, keyed by field ID
	 */
	initialize: function(options) {
		this.options = options || {}
		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			guard: false
		})
		this.tables = _.map(this.options.tables, unwrapModel)
	},

	render: function() {
		const readOnly = this.options.readOnly
		const label = readOnly ? this.exportLabel() : i18n.get('datasets.fields.chooseFields')
		this.$el.empty().append(template({readOnly, label}))

		var checkedIds = _(this.tables)
			.transform(function(result, filter) {
				let fieldIdentifier = this.model.idsToFieldIdentifier(filter)
				result[fieldIdentifier] = true
			}.bind(this), {})
			.value()

		let options = _.extend({
			checkedIds,
			showVisibleLinkedFields: true
		}, this.options)
		let renderer = new FieldListFieldAndGroupRenderer(options)
		// Use `replaceWith` because each group is contained within its own `tbody`
		this.$('tbody').replaceWith(renderer.getHtml())

		// readOnly set when exporting; don't show buttons or redundant header
		readOnly && this.$('.add-dataset, .panel-header ').remove()

		this.toggleToggleAll()

		this.delegateEvents()

		this.formView.start()

		return this
	},

	toggleRow: function(e) {
		var checkbox = $(e.currentTarget).find('input[type=checkbox]')
		if (checkbox.is(':disabled')) {
			this.showDisabledCheckboxTooltip(checkbox)
			return
		}
		var checked = checkbox[0].checked
		// Toggle change so toggleAll will work if needed
		checkbox.prop('checked', !checked).change()
	},

	showDisabledCheckboxTooltip: function(checkbox) {
		this.disabledCheckboxTooltip = new Tooltip({
			attachTo: checkbox.closest('label'),
			content: i18n.get('datasets.fields.cantChooseField', highlight(i18n.get('datasets.none'))),
			placement: 'above',
			arrow: 'center'
		}).render()
	},

	/**
	 * Toggle the toggle-all checkbox based on whether the other checkboxes
	 * are all checked or not
	 */
	toggleToggleAll: function() {
		var checkboxes = this.$('tbody input[type=checkbox]')
		var hasUnchecked = hasUncheckedCheckbox(checkboxes)
		this.$('.toggle-all').prop('checked', !hasUnchecked)
	},

	clickToggleAll: function() {
		var checked = this.$('.toggle-all')[0].checked
		this.$('tbody input[type=checkbox]:not(:disabled)').prop('checked', checked)
	},

	submit: function() {
		var tablesByFieldIdentifier = _.keyBy(this.tables, table => {
			table = unwrapModel(table)
			Dataset.getFieldIdentifier(table.fieldId, table.visibleLinkedFieldId)
		})

		var fieldIdentifiers = _.map(this.$('tbody input[type=checkbox]:checked'), function(el) {
			return $(el).prop('value')
		}.bind(this))
		var tables = _.map(fieldIdentifiers, fieldIdentifier => {
			return tablesByFieldIdentifier[fieldIdentifier] || new DatasetTable({}, {
				dataset: this.model,
				fieldIdentifier
			})
		})

		this.trigger('done', tables)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.formView, this.sendToTooltip, this.disabledCheckboxTooltip], 'remove')
	},

	sendTo: function(e) {
		var target = $(e.currentTarget)
		const exportURL = this.model.section+'/'+this.model.id+'/fields'
		this.sendToTooltip = new Tooltip({
			attachTo: target,
			content: new SendTo({
				heading: this.$('h1').text(),
				sendToOptions: [].concat(
					SendToOptions.excel({
						excelFn: this.exportTable.bind(this)
					}, true),
					SendToOptions.pdf({exportURL})
				)
			}),
			placement: 'below',
			arrow: 'right',
			extraClass: 'has-bootslide-menu'
		}).render()
	},

	exportTable: function() {
		var opts = {
			label: this.exportLabel(),
			interceptRows: function(rows) {
				_.each(rows, function(r) {
					// 15336 - ensure group headings are exported
					const groupRow = _.get(r[0], 'colspan', 1) > 1
					if (groupRow) { // chop off the last cell to keep consistent cells
						r.pop()
						r[0].colspan = r[0].colspan - 1
					} else {  //omit checkbox column
						r.shift()
					}
				})
			}
		}
		return excelExport.saveTableAsFile(this.$('tbody'), opts)
	},

	exportLabel: function() {
		return this.model.get('label') + ' - ' + i18n.get('datasets.dataset.fields')
	}

}, { getExample })

export default FieldList

FieldList.__filename__ = 'datasets/overview/field-list.js'
